import { useState } from "react";
import { Bed, Bath, Maximize2, Car, ChevronRight } from "lucide-react";

const plans = [
  {
    id: "4bhk",
    label: "4 BHK",
    tagline: "Grand Spaces",
    beds: 4,
    baths: 4,
    area: "3,928-4,839",
    parking: 2,
    highlights: [
      "Ground + 2 upper floors with private lift access",
      "Ground and first floor height : 11'10\" ",
      "Second floor height : 11'4\"",
    ],
  },
  {
    id: "5bhk",
    label: "5 BHK",
    tagline: "Palatial Residence",
    beds: 5,
    baths: 5,
    area: "5,335-6,368",
    parking: 3,
    highlights: [
      "Ground + 2 upper floors with private lift access",
      "Ground and first floor height : 12'4\" ",
      "Second floor height : 12'4\"",
    ],
  }
];

export default function FloorPlans() {
  const [activePlan, setActivePlan] = useState("4bhk");
  const plan = plans.find((p) => p.id === activePlan);

  return (
    <section
      id="floorplans"
      data-testid="floor-plans-section"
      className="py-20 md:py-32 bg-[#FAF9F6]"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-16">
          <p className="overline mb-4">Floor Plans</p>
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl tracking-tight text-[#1A1A1A]">
            Spaces crafted for
            <br />
            <span className="italic text-[#C5A059]">every lifestyle</span>.
          </h2>
          <div className="gold-line mx-auto mt-6" />
        </div>

        {/* Plan Tabs */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {plans.map((p) => (
            <button
              key={p.id}
              data-testid={`floorplan-tab-${p.id}`}
              onClick={() => setActivePlan(p.id)}
              className={`px-8 py-3.5 text-sm uppercase tracking-widest font-medium transition-all duration-300 border ${
                activePlan === p.id
                  ? "bg-[#1A1A1A] text-white border-[#1A1A1A]"
                  : "bg-transparent text-[#5C5C59] border-[#E8E4D9] hover:border-[#C5A059] hover:text-[#C5A059]"
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>

        {/* Plan Detail */}
        {plan && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            {/* Left - Floor plan visual representation */}
            <div className="bg-white border border-[#E8E4D9] p-8 md:p-12">
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h3 className="font-serif text-2xl md:text-3xl text-[#1A1A1A]">
                    {plan.label}
                  </h3>
                  <p className="text-[#C5A059] text-sm uppercase tracking-[0.2em] mt-1">
                    {plan.tagline}
                  </p>
                </div>
                <div className="text-right">
                  <div className="stat-number text-3xl md:text-4xl font-bold">
                    {plan.area}
                  </div>
                  <div className="text-xs text-[#5C5C59] uppercase tracking-wider">
                    Sq.Ft
                  </div>
                </div>
              </div>

              {/* Stats row */}
              <div className="grid grid-cols-4 gap-4 mb-8">
                <div className="text-center p-4 bg-[#FAF9F6]">
                  <Bed size={22} className="mx-auto text-[#C5A059] mb-2" strokeWidth={1.5} />
                  <div className="font-serif text-xl font-bold text-[#1A1A1A]">{plan.beds}</div>
                  <div className="text-xs text-[#5C5C59] uppercase tracking-wider">Beds</div>
                </div>
                <div className="text-center p-4 bg-[#FAF9F6]">
                  <Bath size={22} className="mx-auto text-[#C5A059] mb-2" strokeWidth={1.5} />
                  <div className="font-serif text-xl font-bold text-[#1A1A1A]">{plan.baths}</div>
                  <div className="text-xs text-[#5C5C59] uppercase tracking-wider">Baths</div>
                </div>
                <div className="text-center p-4 bg-[#FAF9F6]">
                  <Maximize2 size={22} className="mx-auto text-[#C5A059] mb-2" strokeWidth={1.5} />
                  <div className="font-serif text-xl font-bold text-[#1A1A1A]">{plan.area}</div>
                  <div className="text-xs text-[#5C5C59] uppercase tracking-wider">Sq.Ft</div>
                </div>
                <div className="text-center p-4 bg-[#FAF9F6]">
                  <Car size={22} className="mx-auto text-[#C5A059] mb-2" strokeWidth={1.5} />
                  <div className="font-serif text-xl font-bold text-[#1A1A1A]">{plan.parking}</div>
                  <div className="text-xs text-[#5C5C59] uppercase tracking-wider">Parking</div>
                </div>
              </div>

              {/* CTA */}
              <a
                href="#contact"
                data-testid="floorplan-enquire-btn"
                className="flex items-center justify-center gap-2 w-full bg-[#C5A059] text-white py-4 uppercase tracking-widest text-sm hover:bg-[#B08D4C] transition-colors duration-300"
              >
                Request Floor Plan
                <ChevronRight size={16} />
              </a>
            </div>

            {/* Right - Highlights */}
            <div>
              <h4 className="font-serif text-xl text-[#1A1A1A] mb-6">Key Highlights</h4>
              <div className="space-y-4">
                {plan.highlights.map((item, i) => (
                  <div
                    key={i}
                    data-testid={`highlight-${i}`}
                    className="flex items-start gap-4 py-3 border-b border-[#E8E4D9]/60"
                  >
                    <div className="w-6 h-6 flex items-center justify-center bg-[#C5A059]/10 text-[#C5A059] text-xs font-bold flex-shrink-0 mt-0.5">
                      {String(i + 1).padStart(2, "0")}
                    </div>
                    <span className="text-[#1A1A1A] text-base leading-relaxed">{item}</span>
                  </div>
                ))}
              </div>

              {/* Additional info */}
              <div className="mt-10 p-6 bg-[#C5A059]/5 border-l-4 border-[#C5A059]">
                <p className="font-serif text-lg text-[#1A1A1A] mb-2">Value Built on Values</p>
                <p className="text-sm text-[#5C5C59] leading-relaxed">
                  Every La Valora residence features premium specifications including
                  engineered wooden flooring, biometric door locks, reinforced shear
                  wall construction, and 10-feet main doors.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
